/**
 * Typed client for the per-user self-edit endpoint (ADR-0146 §3.4):
 *
 *   PATCH /v1/account/me — any role on OWN row; allow-list
 *   `{ name?, notification_preferences? }`. The server's WHERE clause
 *   pins to the resolved `user_id` so a cross-user write is structurally
 *   impossible (the body never carries a `user_id` — the row identity
 *   comes from the session cookie).
 *
 * `notification_preferences` is shallow-merged on the server (the
 * column carries existing keys; the PATCH overrides only the keys it
 * names). Slice 1 accepts ONE key: `escalation_digest_email`. Unknown
 * keys are 400 with `reason='unsupported_preference_key'`.
 *
 * The `GET /v1/auth/me` reader is the existing `real-auth.ts` —
 * extended by ADR-0146 to surface `notification_preferences` on the
 * returned `User`. This module owns the WRITE half only.
 */

import type { User } from '@sill/types';
import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

/** Allowed preference keys (mirrors the API's NOTIFICATION_PREFERENCE_KEYS). */
export type NotificationPreferencePatch = {
  readonly escalation_digest_email?: boolean;
  /** ADR-0210 Slice 4 — weekly agent digest. Default on. */
  readonly agent_weekly_digest_email?: boolean;
  /** Session-hijack "new sign-in location" alert email. Default on. */
  readonly session_geo_anomaly_email?: boolean;
  /** Skill-manifest change alert email. Default on. */
  readonly manifest_change_email?: boolean;
  /**
   * Consolidated Notifications (founder brief 2026-10-20) — personal
   * opt-out for ALL security emails. Default on. The server honours
   * this on the recipient SELECT in
   * `services/api/src/jobs/security-alerts-v2.ts`.
   */
  readonly security_alerts_email?: boolean;
  /**
   * ADR-0263 S3 — personal opt-out for plan-usage threshold emails
   * (80% / 100% per-site soft dimensions). Default on. Honoured by
   * the `job:plan_usage_emails` recipient read.
   */
  readonly plan_usage_email?: boolean;
};

export type PatchMeBody = {
  readonly name?: string;
  readonly notification_preferences?: NotificationPreferencePatch;
};

export type PatchMeResult =
  | { readonly kind: 'ok'; readonly user: User }
  | { readonly kind: 'bad_request'; readonly reason?: string | undefined }
  | { readonly kind: 'rate_limited'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

interface MeResponse {
  readonly user: User;
}

export async function patchMe(
  http: HttpClient,
  body: PatchMeBody,
): Promise<PatchMeResult> {
  try {
    const res = await http.request<MeResponse>({
      method: 'PATCH',
      path: '/v1/account/me',
      body,
    });
    if (res === undefined || res.user === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', user: res.user };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'bad_request') {
        return { kind: 'bad_request', reason: err.server_reason };
      }
      // `forbidden` is not possible on PATCH /v1/account/me by design
      // (any auth role can edit their own row). Surface it as a
      // generic error if the server ever returns one — that would be
      // a server-side regression.
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return {
        kind: 'rate_limited',
        message:
          "You've made too many changes. Wait a minute and try again.",
      };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Profile service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
